import { FC, useEffect, useState } from "react";
import { IonButton, IonCard, IonText } from "@ionic/react";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import {
    requestLoginCode,
    verifyLoginCode,
} from "../../../services/authService";
import { LoginSession } from "../../../redux/models/loginSession";

import "./LoginCodeEntry.css";

export const CODE_LENGTH = 6;
export const RESEND_COOLDOWN_SECONDS = 30;

export interface LoginCodeEntryProps {
    email: string;
    onVerified: (response: AxiosResponse<LoginSession>) => Promise<void>;
}

/** Translation key for a refauth LoginCodeError; anything else is a generic failure. */
const errorKey = (error: unknown) => {
    const code = (error as AxiosError<any>)?.response?.data?.error;
    return typeof code === "string"
        ? `login.login.loginCode.errors.${code}`
        : "general.errorTryAgain";
};

/**
 * Six-digit code entry (SCORZA-661). One real input sits over the six display boxes so iOS
 * "From Mail" AutoFill, Android suggestions and paste all fill every digit at once.
 */
export const LoginCodeEntry: FC<LoginCodeEntryProps> = ({
    email,
    onVerified,
}) => {
    const { t } = useTranslation();
    const [code, setCode] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_SECONDS);

    useEffect(() => {
        if (resendIn <= 0) return;
        const timer = setTimeout(() => setResendIn(resendIn - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendIn]);

    const verify = async (candidate: string) => {
        setIsVerifying(true);
        setErrorMessage(null);
        try {
            const response = await verifyLoginCode(email, candidate);
            await onVerified(response);
        } catch (error) {
            setErrorMessage(t(errorKey(error)));
            setCode("");
        } finally {
            setIsVerifying(false);
        }
    };

    const onChange = (value: string) => {
        const digits = value.replace(/\D/g, "").slice(0, CODE_LENGTH);
        setCode(digits);
        setErrorMessage(null);
        if (digits.length === CODE_LENGTH) {
            verify(digits);
        }
    };

    const resend = async () => {
        setErrorMessage(null);
        setCode("");
        try {
            await requestLoginCode(email);
            setResendIn(RESEND_COOLDOWN_SECONDS);
        } catch (error) {
            setErrorMessage(t(errorKey(error)));
        }
    };

    return (
        <IonCard color="transparent" data-testid="login-code-entry">
            <div className="login-code">
                <IonText>
                    <h3 className="login-code-title">
                        {t("login.login.loginCode.title")}
                    </h3>
                    <p className="login-code-message">
                        {t("login.login.loginCode.message", { email })}
                    </p>
                </IonText>

                <label className="login-code-boxes">
                    <span className="login-code-sr-only">
                        {t("login.login.loginCode.inputLabel")}
                    </span>
                    {Array.from({ length: CODE_LENGTH }, (_, i) => (
                        <span
                            key={i}
                            aria-hidden="true"
                            className={
                                "login-code-box" +
                                (i === code.length
                                    ? " login-code-box-active"
                                    : "") +
                                (errorMessage ? " login-code-box-error" : "")
                            }
                        >
                            {code[i] ?? ""}
                        </span>
                    ))}
                    <input
                        data-testid="login-code-input"
                        className="login-code-input"
                        value={code}
                        onChange={(e) => onChange(e.target.value)}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="[0-9]*"
                        maxLength={CODE_LENGTH}
                        disabled={isVerifying}
                        autoFocus
                    />
                </label>

                {errorMessage && (
                    <p
                        className="login-code-error"
                        data-testid="login-code-error"
                        role="alert"
                    >
                        {errorMessage}
                    </p>
                )}

                <p className="login-code-resend">
                    {resendIn > 0 ? (
                        t("login.login.loginCode.resendIn", {
                            seconds: resendIn,
                        })
                    ) : (
                        <IonButton
                            fill="clear"
                            size="small"
                            onClick={resend}
                            data-testid="login-code-resend"
                        >
                            {t("login.login.loginCode.resend")}
                        </IonButton>
                    )}
                </p>

                <IonButton
                    expand="block"
                    className="uppercase"
                    data-testid="login-code-continue"
                    disabled={code.length !== CODE_LENGTH || isVerifying}
                    onClick={() => verify(code)}
                >
                    {t("login.login.loginCode.continue")}
                </IonButton>
            </div>
        </IonCard>
    );
};
